// tslint:disable-next-line no-import-side-effect
import 'core-js/stable/array/find';
// tslint:disable-next-line no-import-side-effect
import 'core-js/stable/array/find-index';

import {$$} from './util/$$';

type Listener = (name: string | undefined) => void;
type UnRegister = () => void;
type Result = {
    order: number;
    breakpoint: string;
    element: HTMLElement;
};

const CLASS_NAME: string = '.z-breakpoint';
const NAME_ATTRIBUTE: string = 'data-breakpoint';
const ORDER_ATTRIBUTE: string = 'data-order';

export class Breakpoint {
    private readonly _breakpoints: Result[];
    private _counter: number = 0;
    private _current: string | undefined;
    private _listeners: { [name: string]: Listener } = {};

    private constructor() {
        this._breakpoints = this.getBreakpoints($$(CLASS_NAME));
        this._current = this.current();

        this.addEventListeners();
    }

    private static _instance: Breakpoint | undefined;

    private static get instance(): Breakpoint {
        if (this._instance === undefined) {
            this._instance = new Breakpoint();
        }

        return this._instance;
    }

    // tslint:disable-next-line function-name
    public static listen(listener: Listener): UnRegister {
        return this.instance.addListener(listener);
    }

    // tslint:disable-next-line function-name
    public static current(): string | undefined {
        return this.instance._current;
    }

    // tslint:disable-next-line function-name
    public static smaller(breakpoint: string): boolean {
        return this.instance.compare(breakpoint) === -1;
    }

    // tslint:disable-next-line function-name
    public static larger(breakpoint: string): boolean {
        return this.instance.compare(breakpoint) === 1;
    }

    // tslint:disable-next-line function-name
    public static equal(breakpoint: string): boolean {
        return this.instance.compare(breakpoint) === 0;
    }

    private compare(breakpoint: string): number {
        const currentIndex: number = this._breakpoints.findIndex(
            (result: Result): boolean => {
                return result.breakpoint === this.current();
            },
        );
        const index: number = this._breakpoints.findIndex(
            (result: Result): boolean => {
                return result.breakpoint === breakpoint;
            },
        );

        if (currentIndex > index) {
            return 1;
        } else if (currentIndex < index) {
            return -1;
        }

        return 0;
    }

    private getBreakpoints(elements: HTMLElement[]): Result[] {
        const results: Result[] = elements.map(
            (el: HTMLElement): Result => {
                return {
                    order: parseInt(
                        <string>el.getAttribute(ORDER_ATTRIBUTE),
                        10,
                    ),
                    breakpoint: <string>el.getAttribute(NAME_ATTRIBUTE),
                    element: el,
                };
            },
        );

        results.sort((a: Result, b: Result): number => {
            return a.order > b.order ? 1 : -1;
        });

        return results;
    }

    private addListener(listener: Listener): UnRegister {
        this._counter += 1;

        const key: string = String(this._counter);

        this._listeners[key] = listener;
        this._current = this.current();

        listener(this._current);

        return (): void => {
            // tslint:disable-next-line no-dynamic-delete
            delete this._listeners[key];
        };
    }

    private addEventListeners(): void {
        window.addEventListener('resize', (): void => {
            this.fire();
        });
    }

    private fire(): void {
        const current: string | undefined = this.current();

        if (current === this._current) {
            return;
        }

        this._current = current;

        Object.keys(this._listeners).forEach((key: string): void => {
            this._listeners[key](this._current);
        });
    }

    private current(): string | undefined {
        const result: Result | undefined = this._breakpoints.find(
            (breakpoint: Result): boolean => {
                const styles: CSSStyleDeclaration = window.getComputedStyle(
                    breakpoint.element,
                );

                return styles.getPropertyValue('z-index') === '1';
            },
        );

        if (result === undefined) {
            return;
        }

        return result.breakpoint;
    }
}
